import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Briefcase, CalendarDays, FileCheck2, Users } from "lucide-react";
import { EmployerLayout } from "@/components/employer/portal/EmployerLayout";
import { apiFetch, formatDate, Kicker, Spinner, StatusPill } from "@/components/employer/portal/shared";

const cardReveal = {
    hidden: { opacity: 0, y: 18 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.55, ease: [0.22, 1, 0.36, 1] } }),
};

const StatCard = ({ icon: Icon, label, value, href, testid, index }) => (
    <motion.a
        href={href}
        data-testid={testid}
        custom={index}
        variants={cardReveal}
        initial="hidden"
        animate="visible"
        className="group border border-ink/10 bg-ivory p-6 transition-colors duration-300 hover:border-forest/35 hover:bg-sand"
    >
        <div className="flex items-center justify-between gap-4">
            <Icon size={19} strokeWidth={1.5} className="text-forest" />
            <ArrowUpRight size={15} strokeWidth={1.5} className="text-ink/25 transition-colors group-hover:text-forest" />
        </div>
        <p className="mt-8 font-serif text-4xl font-medium tracking-tight text-ink">{value}</p>
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/45">{label}</p>
    </motion.a>
);

export default function EmployerDashboard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        apiFetch("/employer/dashboard")
            .then((r) => (r.ok ? r.json() : null))
            .then(setData)
            .catch(() => {});
    }, []);

    return (
        <EmployerLayout
            title={data ? `${data.company_name || "Employer"} Overview` : "Employer Overview"}
            subtitle="Your international recruitment command centre — verification, jobs, applicants and interviews in one place."
            actions={
                <a href="/employer/jobs/new" data-testid="dashboard-create-job" className="inline-flex items-center gap-2 bg-forest px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep">
                    Create Job <ArrowRight size={14} strokeWidth={1.5} />
                </a>
            }
        >
            {!data ? <Spinner /> : (
                <div className="space-y-8" data-testid="employer-dashboard">
                    <section className="grid gap-px overflow-hidden border border-ink/10 bg-ink/10 lg:grid-cols-[1.2fr_0.8fr]">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }} className="relative overflow-hidden bg-night p-7 text-ivory sm:p-9">
                            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full border border-champagne/20" />
                            <motion.div
                                aria-hidden="true"
                                className="absolute -right-10 top-8 h-40 w-40 rounded-full border border-champagne/30"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 34, repeat: Infinity, ease: "linear" }}
                            >
                                <span className="absolute left-1/2 top-0 h-2 w-2 -translate-x-1/2 rounded-full bg-champagne" />
                            </motion.div>
                            <Kicker dark>Global recruitment workspace</Kicker>
                            <h2 className="mt-5 max-w-xl font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
                                Build, review and mobilize your workforce with confidence.
                            </h2>
                            <p className="mt-4 max-w-lg text-sm leading-relaxed text-ivory/60">
                                Afrinexa reviews every employer and vacancy before publication, keeping the marketplace trusted for companies and candidates.
                            </p>
                            <div className="mt-8 flex flex-wrap items-center gap-4">
                                <StatusPill status={data.verification_status} type="verification" dark />
                                <span className="text-xs uppercase tracking-[0.18em] text-ivory/45">Profile {data.profile_completion}% complete</span>
                            </div>
                        </motion.div>
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.08 }} className="bg-sand p-7 sm:p-9">
                            <Kicker>Verification</Kicker>
                            <p className="mt-5 font-serif text-2xl font-medium tracking-tight text-ink">
                                {data.verification_status === "verified" ? "Your company is verified." : "Verification is required before jobs can be submitted."}
                            </p>
                            <p className="mt-3 text-sm leading-relaxed text-ink/55">
                                {data.verification_status === "verified"
                                    ? "You can submit vacancies for Afrinexa review and manage applicants as they arrive."
                                    : "Complete your company profile and submit verification to unlock job review submissions."}
                            </p>
                            <a href="/employer/verification" data-testid="dashboard-verification-link" className="mt-7 inline-flex items-center gap-2 border border-forest/30 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:bg-forest hover:text-ivory">
                                View verification <ArrowRight size={14} strokeWidth={1.5} />
                            </a>
                        </motion.div>
                    </section>

                    <section className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-stats">
                        <StatCard icon={Briefcase} label="Total jobs" value={data.jobs.total || 0} href="/employer/jobs" testid="stat-jobs" index={0} />
                        <StatCard icon={FileCheck2} label="Pending review" value={data.jobs.pending_review || 0} href="/employer/jobs" testid="stat-pending" index={1} />
                        <StatCard icon={Users} label="Applicants" value={data.applications.total || 0} href="/employer/applicants" testid="stat-applicants" index={2} />
                        <StatCard icon={CalendarDays} label="Interviews" value={data.interviews.scheduled || 0} href="/employer/interviews" testid="stat-interviews" index={3} />
                    </section>

                    <section className="grid gap-8 xl:grid-cols-2">
                        <div className="border border-ink/10 bg-ivory p-6 sm:p-7">
                            <div className="flex items-center justify-between gap-4">
                                <h2 className="font-serif text-2xl font-medium tracking-tight">Recent jobs</h2>
                                <a href="/employer/jobs" data-testid="dashboard-jobs-link" className="text-xs font-semibold uppercase tracking-[0.16em] text-forest hover:text-champagne">Manage</a>
                            </div>
                            <div className="mt-5">
                                {data.recent_jobs.length === 0 ? (
                                    <p className="border-t border-ink/10 py-10 text-sm text-ink/50">No vacancies yet — create your first draft.</p>
                                ) : data.recent_jobs.map((job) => (
                                    <a key={job.id} href={`/employer/jobs?edit=${job.id}`} data-testid={`dashboard-job-${job.id}`} className="grid grid-cols-[1fr_auto] items-center gap-4 border-t border-ink/10 px-2 py-4 transition-colors hover:bg-sand">
                                        <span className="min-w-0">
                                            <span className="block truncate font-medium text-ink">{job.title}</span>
                                            <span className="block text-xs text-ink/45">{job.city ? `${job.city}, ` : ""}{job.country} · {formatDate(job.created_at)}</span>
                                        </span>
                                        <StatusPill status={job.status} />
                                    </a>
                                ))}
                                <div className="border-t border-ink/10" />
                            </div>
                        </div>

                        <div className="border border-ink/10 bg-ivory p-6 sm:p-7">
                            <div className="flex items-center justify-between gap-4">
                                <h2 className="font-serif text-2xl font-medium tracking-tight">Recent applicants</h2>
                                <a href="/employer/applicants" data-testid="dashboard-applicants-link" className="text-xs font-semibold uppercase tracking-[0.16em] text-forest hover:text-champagne">View all</a>
                            </div>
                            <div className="mt-5">
                                {data.recent_applicants.length === 0 ? (
                                    <p className="border-t border-ink/10 py-10 text-sm text-ink/50">Applicants will appear here after your roles are published.</p>
                                ) : data.recent_applicants.map((app) => (
                                    <a key={app.id} href={`/employer/applicants?open=${app.id}`} data-testid={`dashboard-applicant-${app.id}`} className="grid grid-cols-[1fr_auto] items-center gap-4 border-t border-ink/10 px-2 py-4 transition-colors hover:bg-sand">
                                        <span className="min-w-0">
                                            <span className="block truncate font-medium text-ink">{app.full_name}</span>
                                            <span className="block truncate text-xs text-ink/45">{app.job_title} · {app.profession} · {formatDate(app.created_at)}</span>
                                        </span>
                                        <StatusPill status={app.status} type="application" />
                                    </a>
                                ))}
                                <div className="border-t border-ink/10" />
                            </div>
                        </div>
                    </section>
                </div>
            )}
        </EmployerLayout>
    );
}
